import React, { useEffect, useState } from 'react';
import { Box, Button, InputAdornment, TextField, Typography } from '@mui/material';
import { Search } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useToast } from '../../contexts/ToastContext';
import { placeholderApi, StorePlaceholder } from '../../services/placeholderApi';
import { tokens } from '../../theme/tokens';
import { EmptyState, InlineMessage, LoadingState, SectionCard, StatusChip } from '../ui';

interface PricingOnlyPlaceholdersCardProps {
  /** The integration whose own selection the page edits, when it edits one. */
  integrationId?: string | null;
  /** Ids already in the selection the page shows, so a row reads Selected at once. */
  selectedIds: Set<string>;
}

const ORIGIN_KEY: Record<StorePlaceholder['origin'], string> = {
  platform_sheet: 'originPlatform',
  private_sheet: 'originPrivate',
  store_listing: 'originListing',
};

/**
 * The product picker's "Pricing-only placeholders" section (Jordan, store
 * shell, Products). A placeholder is a product a vendor sheet or the store's own
 * listing named before the catalog knew it. Selecting one only ever prices
 * (price, compare-at and rebate) a listing the store already has: Motzu never
 * creates a listing for it or writes its title, photos or specs.
 */
const PricingOnlyPlaceholdersCard: React.FC<PricingOnlyPlaceholdersCardProps> = ({ integrationId, selectedIds }) => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const handle = window.setTimeout(() => setSearch(query.trim()), 300);
    return () => window.clearTimeout(handle);
  }, [query]);

  const listKey = ['store-placeholders', search, integrationId || null];
  const { data, isLoading, isError } = useQuery({
    queryKey: listKey,
    queryFn: () => placeholderApi.list({ search, integrationId }),
  });

  const add = useMutation({
    mutationFn: (id: string) => placeholderApi.select([id], integrationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-placeholders'] });
      queryClient.invalidateQueries({ queryKey: ['store-sync-selection'] });
      showToast(t('store.placeholders.added'), 'success');
    },
    onError: (error: unknown) => {
      showToast(error instanceof Error ? error.message : t('store.placeholders.addError'), 'error');
    },
  });

  const rows = data || [];

  return (
    <SectionCard title={t('store.placeholders.title')} subtitle={t('store.placeholders.subtitle')} dense padded>
      <InlineMessage severity="info">{t('store.placeholders.rule')}</InlineMessage>
      <TextField
        size="small"
        placeholder={t('store.placeholders.search')}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        sx={{ mb: 2, width: { xs: '100%', sm: 320 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
      />
      {isLoading && <LoadingState />}
      {isError && <InlineMessage severity="error">{t('store.placeholders.loadError')}</InlineMessage>}
      {!isLoading && !isError && rows.length === 0 && (
        <EmptyState size="compact" title={t('store.placeholders.emptyTitle')} description={t('store.placeholders.emptyDescription')} />
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {rows.map((row) => {
          const selected = row.selected || selectedIds.has(row.id);
          return (
            <Box
              key={row.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1,
                borderTop: `1px solid ${tokens.line.hairline}`,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {[row.brand, row.model, row.sku ? `SKU ${row.sku}` : null, row.upc ? `UPC ${row.upc}` : null].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              <StatusChip label={t(`store.placeholders.${ORIGIN_KEY[row.origin]}`)} tone="neutral" size="small" />
              {selected ? (
                <StatusChip label={t('store.placeholders.selected')} tone="positive" size="small" />
              ) : (
                <Button size="small" variant="outlined" disabled={add.isPending} onClick={() => add.mutate(row.id)}>
                  {t('store.placeholders.addToSync')}
                </Button>
              )}
            </Box>
          );
        })}
      </Box>
    </SectionCard>
  );
};

/** The chip on the picker's filter row that opens the section. */
export const PricingOnlyPlaceholdersToggle: React.FC<{ open: boolean; onToggle: () => void }> = ({ open, onToggle }) => {
  const { t } = useAppTranslation();
  return (
    <Button
      size="small"
      variant={open ? 'contained' : 'outlined'}
      onClick={onToggle}
      aria-pressed={open}
      sx={{ height: 38, whiteSpace: 'nowrap' }}
    >
      {t('store.placeholders.title')}
    </Button>
  );
};

export default PricingOnlyPlaceholdersCard;
